'use client';

import { cn, handleFormSubmitApiError } from '@/lib/utils';
import { contactSchema } from '@/lib/validationSchema';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslations } from 'next-intl';
import { useForm } from 'react-hook-form';
import * as z from 'zod';
import Input from '../ui/Input';
import Select from '../ui/Select';
import Textarea from '../ui/Textarea';
import Button from '../ui/Button';
import ErrorText from '../ui/ErrorText';
import { useState } from 'react';
import FormSuccess from '../blocks/FormSuccess';
import { postData } from '@/lib/apiHelpers';
import { topics } from '@/constants';

type FormData = z.infer<typeof contactSchema>;

export default function ContactForm({ className }: { className?: string }) {
    const t = useTranslations();
    const [isSuccessful, setIsSuccessful] = useState<boolean>(false);
    const [submitting, setSubmitting] = useState<boolean>(false);

    const {
        handleSubmit,
        register,
        reset,
        formState: { errors },
    } = useForm<FormData>({
        resolver: zodResolver(contactSchema),
    });

    const onSubmit = async (formData: FormData) => {
        setSubmitting(true);
        try {
            await postData('/form/contact-form', formData);
            setIsSuccessful(true);
            reset();
        } catch (error) {
            handleFormSubmitApiError(error, t);
        } finally {
            setSubmitting(false);
        }
    };

    const getFieldError = (fieldName: keyof FormData) =>
        errors[fieldName]?.message && errors[fieldName]?.message !== 'Required'
            ? t(`FieldErrors.${errors[fieldName]?.message}`)
            : '';

    return isSuccessful ? (
        <FormSuccess className="w-full" onClickBack={() => setIsSuccessful(false)} />
    ) : (
        <form
            className={cn('get-offer-form-trigger flex flex-col relative w-full', className)}
            onSubmit={handleSubmit(onSubmit)}
        >
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 lg:gap-12">
                <Input
                    {...register('name')}
                    type="text"
                    name="name"
                    id="name"
                    placeholder={t('Form.name')}
                    autoComplete="false"
                    required
                    error={getFieldError('name')}
                />
                <Input
                    {...register('email')}
                    type="email"
                    name="email"
                    id="email"
                    placeholder={t('Form.email')}
                    autoComplete="false"
                    required
                    error={getFieldError('email')}
                />
                <Input
                    {...register('phone')}
                    type="phone"
                    name="phone"
                    id="phone"
                    placeholder={t('Form.phone')}
                    autoComplete="false"
                    required
                    error={getFieldError('phone')}
                />
                <Select {...register('topic')} options={topics(t)} placeholder={t('Form.topic')} />
            </div>
            <Textarea
                {...register('message')}
                className="w-full mt-3 lg:mt-12"
                name="message"
                id="message"
                placeholder={t('Form.message')}
                autoComplete="false"
                rows={5}
            />
            {errors && (
                <div className="flex flex-col gap-1 text-left h-12 pt-6">
                    <ErrorText error={getFieldError('name')} />
                    <ErrorText error={getFieldError('email')} />
                    <ErrorText error={getFieldError('phone')} />
                    <ErrorText error={getFieldError('topic')} />
                    <ErrorText error={getFieldError('message')} />
                </div>
            )}
            <Button
                className="self-end"
                variant="primary"
                title={t('Buttons.submit')}
                type="submit"
                submitting={submitting}
                disabled={submitting}
            />
        </form>
    );
}
